import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import { captureUserActionError } from 'src/utils/errorReporting';
import { fetchPdfFile } from 'src/utils/fetchPdfFile';

export const usePreparePdf = (handleCloseReportViewer: () => void) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [blobData, setBlobData] = useState<Blob>();
  const { t } = useTranslation(I18NextNS.COMMON);

  const {
    i18n: { language },
  } = useTranslation();

  const { session } = useRemoteBoundStore();

  const clearPdfState = useCallback(() => {
    setPdfUrl('');
    setBlobData(undefined);
  }, []);

  const preparePdf = useCallback(async () => {
    try {
      if (session.order?.ReportFile?.link) {
        const result = await fetchPdfFile(session.order?.ReportFile?.link);
        const blob = result.blob;
        const blobUrl = result.blobUrl;

        setBlobData(blob);
        setPdfUrl(blobUrl);
      } else {
        clearPdfState();
        throw new Error(t('message.error.reportNotFound'));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      captureUserActionError(error, 'pdf_preparation_failed', {
        extras: {
          errorMessage,
        },
      });

      clearPdfState();

      Api.alertBox({
        title: t('bootbox.title.Error'),
        message: `${t('message.error.failedToLoadReport')}`,
        locale: language,
        type: 'error',
        callback: () => {
          handleCloseReportViewer();
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearPdfState, t, language, handleCloseReportViewer]);

  useEffect(() => {
    preparePdf();
  }, [preparePdf]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  return { pdfUrl, blobData };
};
